import React from 'react';
import { Box, Card, Typography } from '@mui/material';
import CottageOutlined from '@mui/icons-material/CottageOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import SpaOutlined from '@mui/icons-material/SpaOutlined';
import East from '@mui/icons-material/East';

import journeyDelivered from '../../../assets/landing/journey-delivered.png';
import journeyFed from '../../../assets/landing/journey-fed.png';
import journeyVillage from '../../../assets/landing/journey-village.png';
import { v2Colors } from '../../design/theme';

const DISPLAY_FONT = '"Foregen", "Roboto Slab", Georgia, serif';
const CARD_TITLE_FONT = '"Roboto", "Helvetica Neue", Arial, sans-serif';

const STEPS = [
  {
    step: '01',
    title: 'Raised in the Village',
    body:
      'Our partner farmers raise birds on open land in 200+ villages across Telangana, Andhra Pradesh, Tamil Nadu and Maharashtra, the way it’s been done for generations.',
    image: journeyVillage,
    imageAlt: 'Country chickens raised under mango trees in a village farm',
    Icon: CottageOutlined,
  },
  {
    step: '02',
    title: 'Fed Naturally, Never Forced',
    body:
      'No antibiotics. No growth hormones. No banned substances. Tested by an independent NABL-accredited lab before it reaches you.',
    image: journeyFed,
    imageAlt: 'Chicken feeding naturally on grain',
    Icon: SpaOutlined,
  },
  {
    step: '03',
    title: 'Delivered Fresh to Your Family',
    body:
      'Procured 3–8 days before your order. From the farmer’s hands to your kitchen, fully traceable.',
    image: journeyDelivered,
    imageAlt: 'Country Chicken Co delivery at your doorstep',
    Icon: LocalShippingOutlined,
  },
];

function JourneyCard({ step, title, body, image, imageAlt, Icon }) {
  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: v2Colors.white,
        border: '1px solid',
        borderColor: 'rgba(165, 42, 60, 0.08)',
        boxShadow: '0 4px 24px rgba(26, 19, 15, 0.06)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component="img"
        src={image}
        alt={imageAlt}
        sx={{
          width: '100%',
          height: { xs: 200, sm: 220, md: 200, lg: 220 },
          objectFit: 'cover',
          display: 'block',
        }}
      />
      <Box
        sx={{
          px: { xs: 2, md: 2.25 },
          pt: 2,
          pb: 2.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: v2Colors.primary,
              color: v2Colors.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden
          >
            <Icon sx={{ fontSize: 22 }} />
          </Box>
          <Typography
            component="span"
            aria-hidden
            sx={{
              fontFamily: DISPLAY_FONT,
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '2.75rem' },
              lineHeight: 1,
              color: v2Colors.selectedFill,
              letterSpacing: '-0.02em',
            }}
          >
            {step}
          </Typography>
        </Box>
        <Typography
          component="h3"
          sx={{
            fontFamily: CARD_TITLE_FONT,
            fontWeight: 700,
            fontSize: { xs: '1.05rem', md: '1.1rem' },
            color: v2Colors.heading,
            lineHeight: 1.25,
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            flex: { xs: 'none', md: 1 },
            fontSize: { xs: '0.8rem', md: '0.85rem' },
            lineHeight: 1.55,
            color: v2Colors.textMuted,
          }}
        >
          {body}
        </Typography>
      </Box>
    </Card>
  );
}

function StepArrow() {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        px: 0.5,
        color: v2Colors.primaryMuted,
        alignSelf: 'center',
      }}
      aria-hidden
    >
      <East sx={{ fontSize: 28 }} />
    </Box>
  );
}

export default function HonestBiteJourneySection() {
  return (
    <Box
      component="section"
      aria-labelledby="honest-bite-journey-heading"
      sx={{ mb: { xs: 2, md: 4 } }}
    >
      <Typography
        id="honest-bite-journey-heading"
        component="h2"
        sx={{
          fontFamily: DISPLAY_FONT,
          fontWeight: 700,
          fontSize: { xs: '1.35rem', sm: '1.5rem', md: '1.65rem' },
          lineHeight: 1.2,
          color: v2Colors.heading,
          textAlign: 'center',
          mb: { xs: 2.5, md: 3 },
          letterSpacing: '-0.02em',
        }}
      >
        The journey behind every{' '}
        <Box component="span" sx={{ color: v2Colors.primary }}>
          honest
        </Box>{' '}
        bite.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: { xs: 2, md: 0 },
        }}
      >
        {STEPS.map((item, index) => (
          <React.Fragment key={item.step}>
            <Box
              sx={{
                flex: { xs: 'none', md: 1 },
                minWidth: 0,
                display: 'flex',
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <JourneyCard {...item} />
            </Box>
            {index < STEPS.length - 1 ? <StepArrow /> : null}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
